import { useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';

import { SessionProvider } from './auth/SessionContext';
import { BusinessSessionScope } from './auth/BusinessSessionScope';
import { useSession } from './auth/useSession';
import { NavBar } from './components/NavBar';
import { HeliosShell } from './components/shell/HeliosShell';
import { ScreenEntrance } from './components/shell/ScreenEntrance';
import { navOrder } from './components/shell/navModel';
import { useNavModel } from './components/shell/useNavModel';
import { useLook } from './lib/lookStore';
import { WorkspaceBar } from './components/WorkspaceBar';
import { TrialBanner } from './components/TrialBanner';
import { BookingFeesNotice } from './components/BookingFees';
import { Toasts } from './components/Toasts';

/**
 * Routes that never get the sidebar/tab-bar chrome, even while authenticated.
 * /registro signs the person in on its last question; wrapping it in the
 * chrome at that moment would remount the wizard and lose the finish.
 */
const CHROMELESS_PATHS = new Set(['/entrar', '/cambiar-contrasena', '/pago', '/registro']);

export function isChromeless(pathname: string): boolean {
  return CHROMELESS_PATHS.has(pathname);
}

/** Vintage keeps the sidebar; its screens rise in like the other looks'. */
function VintageLayout() {
  const model = useNavModel();
  const order = useMemo(() => navOrder(model), [model]);
  return (
    <div className="shell">
      <NavBar />
      <main className="shell-main">
        <WorkspaceBar />
        <TrialBanner />
        <BookingFeesNotice />
        <ScreenEntrance order={order}>
          <Outlet />
        </ScreenEntrance>
      </main>
    </div>
  );
}

/** Nav only once a session exists; /entrar and /cambiar-contrasena render with no chrome around them. */
function Shell() {
  const { status, session } = useSession();
  const location = useLocation();
  const look = useLook();
  const isAdminRoute = location.pathname === '/admin' || location.pathname.startsWith('/admin/');
  if (status !== 'authenticated' || isChromeless(location.pathname) || isAdminRoute) return <Outlet />;
  return (
    <BusinessSessionScope identity={`${session?.businessUserId}:${session?.businessId}:${session?.expiresAt}`}>
    {look === 'vintage' ? (
      <VintageLayout />
    ) : (
      <HeliosShell />
    )}
    </BusinessSessionScope>
  );
}

/**
 * The root route's component. `SessionProvider` lives here (inside the router
 * tree) specifically so it can call `useNavigate` for the 401 redirect; it
 * can't do that from outside `RouterProvider`.
 */
export function RootLayout() {
  const navigate = useNavigate();
  return (
    <SessionProvider onUnauthorized={() => void navigate({ to: '/entrar', replace: true })}>
      <Shell />
      <Toasts />
    </SessionProvider>
  );
}
